import { computed } from 'vue';
import { useRoute } from 'vue-router';

export type HeaderContext = 'ipa' | 'pdp';

/**
 * Determines which header to show based on the current route
 * IPA Header: Company pages (/, /about, /our-work, /contact, /partners, /insights)
 * PDP Header: Platform pages (/platform, /e-learning, /access-options, /blog, /profile, course/lesson pages)
 */
export const useHeaderContext = () => {
  const route = useRoute();

  const headerContext = computed<HeaderContext>(() => {
    // Normalize path by removing trailing slash (except for root '/')
    let path = route.path;
    if (path !== '/' && path.endsWith('/')) {
      path = path.slice(0, -1);
    }

    // PDP (Polo Development Platform) pages - e-learning platform
    // Note: /lms/* routes use their own 'lms' layout with LmsMainNav, so they don't use this header system
    const pdpRoutes = [
      '/platform',
      '/courses',
      '/subscriptions',
      '/premium-packages',
      '/premium-subscriptions',
      '/blog',
    ];

    // Check if current path matches any PDP route
    if (pdpRoutes.includes(path)) {
      return 'pdp';
    }

    // Everything else defaults to IPA (Company) header
    // This includes: /, /about, /our-work, /contact, /blog, /courses, /subscriptions, etc.
    // These are all public-facing company/marketing pages
    return 'ipa';
  });

  const isIpaContext = computed(() => headerContext.value === 'ipa');
  const isPdpContext = computed(() => headerContext.value === 'pdp');

  return {
    headerContext,
    isIpaContext,
    isPdpContext,
  };
};
